// Emojis are not accepted anywhere in the product. They are stripped at the
// input layer (so a typed or pasted emoji never sticks) and again in the axios
// request interceptor, so nothing reaches the backend from screens that build
// their payload by hand.

// A pictographic base plus whatever can extend it: presentation selectors
// (FE0E/FE0F), skin tone modifiers (1F3FB-1F3FF), enclosing keycap (20E3) and
// ZWJ-joined parts (200D), so a family or a thumbs-up with a tone is removed as
// one unit. ZWJ is only consumed when it joins two pictographics, so it stays
// intact inside Indic text where it is a real joiner. The 1F1E6-1F1FF range is
// the regional indicators that make up flags.
const EMOJI_SEQUENCE_REGEX =
  /(?:\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}])(?:\uFE0E|\uFE0F|\u{20E3}|[\u{1F3FB}-\u{1F3FF}]|\u200D(?:\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]))*/gu;

// Keycaps are built on a plain digit or # / *, so they need their own pass.
const KEYCAP_REGEX = /[0-9#*]\uFE0F?\u{20E3}/gu;

// Presentation selectors left behind once their base is gone.
const VARIATION_SELECTOR_REGEX = /\uFE0E|\uFE0F/g;

// Emoji-capable but typed as text far more often - e.g. drug brand names.
// Only kept when they arrive bare; with a presentation selector attached they
// are being used as emoji and go.
const KEEP_AS_TEXT = ["©", "®", "™", "ℹ"];

// Credentials are matched against what was already stored, so sanitising them
// would silently break sign-in for anyone whose password contains an emoji.
const isCredentialKey = (key) =>
  typeof key === "string" && key.toLowerCase().includes("password");

export const stripEmojis = (value) => {
  if (typeof value !== "string" || !value) return value;

  return value
    .replace(KEYCAP_REGEX, "")
    .replace(EMOJI_SEQUENCE_REGEX, (match) =>
      KEEP_AS_TEXT.includes(match) ? match : "",
    )
    .replace(VARIATION_SELECTOR_REGEX, "");
};

const stripFormData = (formData) => {
  const entries = [];
  let changed = false;

  formData.forEach((entry, key) => {
    // Files and blobs pass through untouched.
    const cleaned = typeof entry === "string" ? stripEmojis(entry) : entry;
    if (cleaned !== entry) changed = true;
    entries.push([key, cleaned]);
  });

  if (!changed) return formData;

  const next = new FormData();
  entries.forEach(([key, entry]) => next.append(key, entry));
  return next;
};

// Walks request payloads. Arrays and plain objects are rebuilt; anything else
// with a prototype of its own (Blob, File, Date, URLSearchParams) is left alone.
export const stripEmojisDeep = (value) => {
  if (typeof value === "string") return stripEmojis(value);

  if (Array.isArray(value)) return value.map(stripEmojisDeep);

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    return stripFormData(value);
  }

  if (value && typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;

    const out = {};
    Object.keys(value).forEach((key) => {
      out[key] = isCredentialKey(key) ? value[key] : stripEmojisDeep(value[key]);
    });
    return out;
  }

  return value;
};

// Drop-in for an input's onChange: cleans the value in place, then forwards the
// original event so target.name and friends survive.
export const withoutEmojis = (handler) => (event) => {
  const value = event?.target?.value;

  if (typeof value === "string") {
    const cleaned = stripEmojis(value);
    if (cleaned !== value) event.target.value = cleaned;
  }

  return handler?.(event);
};
